import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { viewBookingDetails } from '../../slice/bookingSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'

const BookingDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    // Get bookingId from state
    const bookingId = location.state?.bookingId

    const { bookingDetail } = useSelector(state => state.booking)

    useEffect(() => {
        if (!bookingId) {
            navigate('/bookings')
            return
        }
        dispatch(viewBookingDetails({ id: bookingId }))
    }, [bookingId, dispatch, navigate])

    if (!bookingDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/bookings')}
                >
                    Go Back to Bookings
                </button>
            </div>
        )
    }

    const {
        booking_id,
        start_time,
        end_time,
        is_accepted,
        status,
        is_rescheduled,
        rescheduled_by,
        is_cancelled,
        cancelled_by,
        commission_rate,
        session_amount,
        is_active,
        is_deleted,
        createdAt,
        updatedAt,
        tutor,
        parent,
        order
    } = bookingDetail

    // Calculate duration
    const getDuration = () => {
        if (start_time && end_time) {
            const start = moment(start_time)
            const end = moment(end_time)
            const duration = moment.duration(end.diff(start))
            const hours = Math.floor(duration.asHours())
            const minutes = duration.minutes()
            return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`
        }
        return 'NA'
    }

    // Get status badge styling
    const getStatusBadge = (status) => {
        const statusConfig = {
            ongoing: { bg: 'success', text: 'Ongoing' },
            completed: { bg: 'info', text: 'Completed' },
            cancelled: { bg: 'danger', text: 'Cancelled' },
            pending: { bg: 'warning', text: 'Pending' },
            scheduled: { bg: 'primary', text: 'Scheduled' }
        }

        const config = statusConfig[status] || { bg: 'secondary', text: status }

        return (
            <span className={`badge bg-${config.bg}`}>
                {config.text}
            </span>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Booking Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/bookings')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Booking ID Header Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1 fw-semibold">Session ID: {booking_id}</h5>
                            <p className="mb-0 text-muted">Booking Reference Number : {order?.order_id}</p>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            {getStatusBadge(status)}
                            {/* {is_accepted === 1 && (
                                <span className="badge bg-success">Accepted</span>
                            )}
                            {is_accepted === 0 && (
                                <span className="badge bg-warning text-dark">Pending Acceptance</span>
                            )}
                            {is_rescheduled === 1 && (
                                <span className="badge bg-info">
                                    <i className="fe-refresh-cw" style={{ fontSize: '10px' }}></i> Rescheduled
                                </span>
                            )}
                            {is_cancelled === 1 && (
                                <span className="badge bg-danger">Cancelled</span>
                            )}
                            {is_deleted === 1 && (
                                <span className="badge bg-dark">Deleted</span>
                            )} */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tutor and Parent Info */}
            <div className="row mb-4">
                {/* Tutor Card */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex justify-content-between align-items-center" style={{ borderRadius: '8px 8px 0 0', height: '48px', padding: '0 16px' }}>
                            <span>Tutor Information</span>
                            <div className="d-flex gap-2">
                                {tutor?.is_premium_verified === 1 && (
                                    <span
                                        style={{
                                            backgroundColor: '#fffbeb',
                                            color: '#d97706',
                                            border: '1px solid #fde68a',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <i className="fe-star" style={{ fontSize: '12px' }}></i>
                                        Premium
                                    </span>
                                )}
                                {tutor?.is_mohre_verification === 1 && (
                                    <span
                                        style={{
                                            backgroundColor: '#f0fdf4',
                                            color: '#16a34a',
                                            border: '1px solid #bbf7d0',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <i className="fe-check" style={{ fontSize: '12px' }}></i>
                                        MoHRE Verified
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={tutor?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="Tutor"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                                    }}
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '3px solid #f8f9fa'
                                    }}
                                />
                                <div>
                                    <h6 className="mb-1 fw-semibold">
                                        {tutor?.first_name} {tutor?.last_name}
                                    </h6>
                                    <p className="mb-0 text-muted small">ID: {tutor?.id}</p>
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{tutor?.email || 'NA'}</strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Phone</small>
                                <strong>
                                    {tutor?.country_code && tutor?.phone_number
                                        ? `${tutor.country_code} ${tutor.phone_number}`
                                        : 'NA'}
                                </strong>
                            </div>

                            {tutor?.pricing && (
                                <div>
                                    <small className="text-muted d-block mb-1">Pricing</small>
                                    <strong>{tutor.pricing.single_session_price}</strong>
                                </div>
                            )}

                            {tutor?.badges && tutor.badges.length > 0 && (
                                <div className="mt-3">
                                    <small className="text-muted d-block mb-2">Badges</small>
                                    <div className="d-flex flex-wrap gap-2">
                                        {tutor.badges.map((badge, index) => (
                                            <span key={index} className="badge bg-primary">
                                                {badge}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Parent Card */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0', height: '48px', padding: '0 16px' }}>
                            Parent Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={parent?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="Parent"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                                    }}
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '3px solid #f8f9fa'
                                    }}
                                />
                                <div>
                                    <h6 className="mb-1 fw-semibold">
                                        {parent?.first_name} {parent?.last_name}
                                    </h6>
                                    <p className="mb-0 text-muted small">ID: {parent?.id}</p>
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{parent?.email || 'NA'}</strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Phone</small>
                                <strong>
                                    {parent?.country_code && parent?.phone_number
                                        ? `${parent.country_code} ${parent.phone_number}`
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Booking Details */}
            <div className="row">
                {/* Session Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0', height: '48px', padding: '0 16px' }}>
                            Session Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Start Time</small>
                                <strong>
                                    {start_time ? start_time : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">End Time</small>
                                <strong>
                                    {end_time ? end_time : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Duration</small>
                                {/* <strong>{getDuration()}</strong> */}
                                <strong>1 hr</strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Status</small>
                                <div className="mt-1">
                                    {getStatusBadge(status)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0', height: '48px', padding: '0 16px' }}>
                            Payment Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Session Amount</small>
                                <strong>
                                    {order?.total_amount
                                        ? `AED ${parseFloat(order?.total_amount).toFixed(2)}`
                                        : 'NA'}
                                </strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Commission Rate</small>
                                <strong>
                                    {order?.commission_rate
                                        ? `${parseFloat(order?.commission_rate).toFixed(2)}%`
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Booking Status */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0', height: '48px', padding: '0 16px' }}>
                            Booking Status
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Acceptance Status</small>
                                <div className="mt-1">
                                    {is_accepted === 1 ? (
                                        <span className="badge bg-success">Accepted</span>
                                    ) : (
                                        <span className="badge bg-warning text-dark">Pending</span>
                                    )}
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Rescheduled</small>
                                <div className="mt-1">
                                    {is_rescheduled === 1 ? (
                                        <>
                                            <span className="badge bg-info">Yes</span>
                                            {rescheduled_by && (
                                                <div className="mt-1">
                                                    <small className="text-muted">by: {rescheduled_by}</small>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <span className="badge bg-secondary">No</span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Cancelled</small>
                                <div className="mt-1">
                                    {is_cancelled === 1 ? (
                                        <>
                                            <span className="badge bg-danger">Yes</span>
                                            {cancelled_by && (
                                                <div className="mt-1">
                                                    <small className="text-muted">by: {cancelled_by}</small>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <span className="badge bg-secondary">No</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Record Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0', height: '48px', padding: '0 16px' }}>
                            Record Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Created At</small>
                                <strong>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Active Status</small>
                                <div className="mt-1">
                                    {is_active === 1 ? (
                                        <span className="badge bg-success">Active</span>
                                    ) : (
                                        <span className="badge bg-secondary">Inactive</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BookingDetail;